import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Maximize2, X, Image as ImageIcon, Video } from 'lucide-react';

interface GalleryItem {
    id: number;
    type: 'image' | 'video';
    url: string;
    thumbnail: string;
    title: string;
    size: 'small' | 'medium' | 'large' | 'tall' | 'wide';
}

const galleryItems: GalleryItem[] = [
    {
        id: 1,
        type: 'video',
        url: '/videos/Galeria/E1.mp4',
        thumbnail: '',
        title: '',
        size: 'large',
    },
    {
        id: 2,
        type: 'video',
        url: '/videos/Galeria/E2.mp4',
        thumbnail: '',
        title: '',
        size: 'medium',
    },
    {
        id: 3,
        type: 'image',
        url: '/videos/Galeria/E3.jpeg',
        thumbnail: '/videos/Galeria/E3.jpeg',
        title: '',
        size: 'tall',
    },
    {
        id: 4,
        type: 'image',
        url: '/videos/Galeria/E4.jpeg',
        thumbnail: '/videos/Galeria/E4.jpeg',
        title: '',
        size: 'medium',
    },
    {
        id: 5,
        type: 'image',
        url: '/videos/Galeria/E5.jpeg',
        thumbnail: '/videos/Galeria/E5.jpeg',
        title: '',
        size: 'wide',
    },
    {
        id: 6,
        type: 'image',
        url: '/videos/Galeria/E6.jpeg',
        thumbnail: '/videos/Galeria/E6.jpeg',
        title: '',
        size: 'wide',
    },
    {
        id: 7,
        type: 'image',
        url: '/videos/Galeria/E7.jpeg',
        thumbnail: '/videos/Galeria/E7.jpeg',
        title: '',
        size: 'medium',
    },
    {
        id: 8,
        type: 'image',
        url: '/videos/Galeria/E8.jpeg',
        thumbnail: '/videos/Galeria/E8.jpeg',
        title: '',
        size: 'small',
    },
    {
        id: 9,
        type: 'video',
        url: '/videos/Galeria/E9.mp4',
        thumbnail: '',
        title: '',
        size: 'wide',
    },
];

const sizeClasses = {
    small: 'col-span-1 row-span-1 aspect-square',
    medium: 'col-span-1 row-span-1 aspect-square',
    large: 'md:col-span-2 md:row-span-2 aspect-square',
    tall: 'col-span-1 row-span-2 aspect-square sm:aspect-[1/2]',
    wide: 'md:col-span-2 row-span-1 aspect-square sm:aspect-[2/1]',
};

const GalleryItemCard: React.FC<{ item: GalleryItem; onClick: (item: GalleryItem) => void }> = ({ item, onClick }) => {
    const [isHovered, setIsHovered] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            whileHover={{ y: -5 }}
            transition={{ duration: 0.4 }}
            className={`relative overflow-hidden rounded-2xl group cursor-pointer w-full h-full`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => onClick(item)}
        >
            {/* Background Media */}
            <div className="absolute inset-0 w-full h-full">
                {item.type === 'video' ? (
                    <video
                        src={item.url}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        muted
                        loop
                        playsInline
                        autoPlay
                    />
                ) : (
                    <img
                        src={item.url}
                        alt={item.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                )}
            </div>

            {/* Video Preview on Hover (if it were a real local video file) */}
            {/* {item.type === 'video' && isHovered && (
        <video
          ref={videoRef}
          src={item.url}
          className="absolute inset-0 w-full h-full object-cover z-10"
          autoPlay
          muted
          loop
        />
      )} */}

            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300 z-20" />

            {/* Gold Border Glow */}
            <div className="absolute inset-0 border border-gold/0 group-hover:border-gold/30 rounded-2xl transition-colors duration-300 z-30" />
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-20 pointer-events-none"
                style={{ boxShadow: 'inset 0 0 30px hsl(43 74% 53% / 0.2)' }} />

            {/* Content */}
            <div className="absolute bottom-0 left-0 right-0 p-6 z-40 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-gold/20 backdrop-blur-md flex items-center justify-center text-gold border border-gold/30">
                        {item.type === 'video' ? <Video size={14} /> : <ImageIcon size={14} />}
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-gold font-bold">
                        {item.type === 'video' ? 'Vídeo' : 'Foto'}
                    </span>
                </div>
                <div className="mt-4 flex items-center gap-2 text-gold opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100 translate-x-[-10px] group-hover:translate-x-0">
                    <span className="text-xs font-bold uppercase tracking-wider">Ver {item.type === 'video' ? 'Vídeo' : 'Foto'}</span>
                    <Play size={12} fill="currentColor" className={item.type === 'video' ? 'block' : 'hidden'} />
                    <Maximize2 size={12} className={item.type === 'image' ? 'block' : 'hidden'} />
                </div>
            </div>
        </motion.div>
    );
};

const GallerySection: React.FC = () => {
    const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);

    return (
        <section className="py-24 sm:py-32 bg-background relative overflow-hidden">
            {/* Background Elements */}
            <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-gold/5 rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

            <div className="container mx-auto px-6 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center mb-20"
                >
                    <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
                        Galeria de Experiências
                    </span>
                    <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary mb-6">
                        O Estilo de Vida <span className="text-gold italic">Náutico</span>
                    </h2>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                        Explore momentos reais de nossos alunos e instrutores vivendo a liberdade que só o mar proporciona.
                    </p>
                </motion.div>

                {/* Gallery Container: Carousel on Mobile, Bento Grid on Desktop */}
                <div className="flex sm:grid flex-nowrap sm:flex-wrap sm:grid-cols-2 lg:grid-cols-4 sm:grid-flow-dense gap-6 overflow-x-auto sm:overflow-x-visible pb-12 sm:pb-0 snap-x snap-mandatory scrollbar-hide px-4 sm:px-0">
                    {galleryItems.map((item) => (
                        <div
                            key={item.id}
                            className={`
                                min-w-[85vw] sm:min-w-0 snap-center sm:snap-align-none flex-shrink-0
                                ${sizeClasses[item.size]}
                            `}
                        >
                            <GalleryItemCard item={item} onClick={setSelectedItem} />
                        </div>
                    ))}
                </div>

                {/* Mobile Scroll Indicator */}
                <div className="flex sm:hidden justify-center gap-2 mt-4">
                    {galleryItems.map((_, idx) => (
                        <div
                            key={idx}
                            className="w-1.5 h-1.5 rounded-full bg-primary/20"
                        />
                    ))}
                </div>

                {/* Lightbox / Modal */}
                <AnimatePresence>
                    {selectedItem && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-primary/95 backdrop-blur-xl"
                            onClick={() => setSelectedItem(null)}
                        >
                            <button
                                className="absolute top-6 right-6 text-white hover:text-gold transition-colors z-[110]"
                                onClick={() => setSelectedItem(null)}
                            >
                                <X size={32} />
                            </button>

                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                className="relative max-w-5xl w-full h-full max-h-[80vh] flex items-center justify-center"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {selectedItem.type === 'image' ? (
                                    <img
                                        src={selectedItem.url}
                                        alt={selectedItem.title}
                                        className="w-full h-full object-contain rounded-lg shadow-2xl"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-black rounded-lg overflow-hidden shadow-2xl">
                                        <video
                                            src={selectedItem.url}
                                            className="max-h-full w-auto"
                                            controls
                                            autoPlay
                                        />
                                    </div>
                                )}

                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
};

export default GallerySection;
